module Meevo.Plugins.Register {
    'use strict';

    export class PromotionalGiftCardsSection extends Meevo.Plugins.Register.RegisterSectionBase {
        clientsWithGiftCards: any[] = [];
        selectedClient?: Model.ClientListItem;
        includeExpired: boolean = false;
        canUseExpired: boolean = false;
        expiredGiftCardSecurityKeyPath?: string;
        giftCardNumber?: string;
        preselectGiftCardId?: string;

        protected meevoSecurityKeys = Meevo.Components.Common.SecurityKeys;

        get $translate() { return this.registerContext.$translate; }
        get $q() { return this.registerContext.$q; }
        get $filter() { return this.registerContext.$filter; }
        get dataService() { return this.registerContext.dataService; }
        get modalService() { return this.registerContext.modalService; }
        get securityService() { return this.registerContext.securityService; }
        get busyService() { return this.registerContext.busyService; }
        get registerGiftCardService() { return this.registerContext.registerGiftCardService; }

        constructor(registerContext: Meevo.Plugins.Register.RegisterContext) {
            super(RegisterSectionTypeEnum.PromotionalGiftCard, registerContext, 'promotionalGiftCards', Register.RegisterSectionCatTypeEnum.Other);

            this.securityService.checkSecurityItem(Meevo.Components.Common.SecurityKeys.REGISTER_REGISTER_ACTION_USEEXPIREDGIFTCARD).then(result => {
                this.canUseExpired = (result.securityAccess === Meevo.Model.SecurityAccessEnum.Grant);
                this.expiredGiftCardSecurityKeyPath = result.securityKeyPath;
            });
        }

        reset() {
            this.giftCardNumber = undefined;
            this.includeExpired = false;
            this.selectedClient = undefined;
            this.clear();
            this.updateClients();
        }

        clear() {
            this.clientsWithGiftCards = [];
        }

        hasItems() {
            return (this.includeExpired && _(this.clientsWithGiftCards).any())
                || _(this.clientsWithGiftCards).findWhere({ hasNonExpired: true }) != undefined;
        }

        cancel() {
            this.registerContext.selectSection(RegisterSectionTypeEnum.Services);
        }

        // Loads the non-liability promotional gift cards for the supplied clients. Defaults to the
        // clients already on the sale (the currently selected register client), and additional clients
        // can be added via the Client type-ahead.
        private addClients(newClientIds: any[]) {
            if (!_(newClientIds).any()) {
                return;
            }

            const request: any = {
                pageNumber: 0,
                itemsPerPage: 1000,
                criteria: {
                    clientIds: newClientIds,
                    includeExpired: !this.canUseExpired || this.includeExpired
                }
            };

            this.dataService.getListPaged('clientgiftcard', request)
                .then((response: any) => {
                    // This pane only deals with non-liability promotional cards; filter client-side.
                    const allowedGiftCards = _(response).filter((card: any) =>
                        this.isNonLiabilityPromo(card) && this.registerGiftCardService.checkGCUsageRestriction(card, this.canUseExpired));
                    this.addGiftCards(allowedGiftCards);
                });
        }

        public addGiftCards(giftCards: any[], isEntered: boolean = false): void {
            const clientIds = Object.keys(_(giftCards).groupBy('clientId'));
            clientIds.forEach((clientId) => {
                const clientIdResolved = clientId.toLowerCase() === "undefined" ? undefined : clientId;
                const clientWithGiftCards: any = _(giftCards).filter(giftCard => giftCard.clientId === clientIdResolved);
                let existingClient = _(this.clientsWithGiftCards).findWhere({ id: clientIdResolved });
                if (!existingClient) {
                    existingClient = {
                        id: clientIdResolved,
                        giftCards: []
                    };

                    existingClient.displayName = clientIdResolved
                        ? `${clientWithGiftCards[0].clientLastName}, ${clientWithGiftCards[0].clientFirstName}`
                        : this.$translate.instant('LABEL_ANONYMOUS_OTHER');

                    this.clientsWithGiftCards.push(existingClient);
                }
                this.addMissingGiftCards(existingClient, clientWithGiftCards, isEntered);
                existingClient.giftCards = this.sort(existingClient.giftCards);
                if (!isEntered || !existingClient.selectedGiftCard) {
                    this.selectGiftCardForClient(existingClient, true);
                }
            });

            this.sortClients();
        }

        private addMissingGiftCards(existingClient, giftCards, isEntered): void {
            giftCards = this.sort(giftCards);

            giftCards.forEach((currentGiftCard) => {
                if (currentGiftCard.clientId != existingClient.id) {
                    return;
                }

                let existingGiftCard = existingClient.giftCards.find(clientGC => clientGC.id === currentGiftCard.id);

                if (!existingGiftCard) {
                    currentGiftCard.originalAmount = currentGiftCard.remainingAmount;
                    existingClient.giftCards.push(currentGiftCard);
                    // Derive this card's eligible items + applied selection from the sale, once, on load.
                    this.buildCardState(currentGiftCard);
                    if (!currentGiftCard.isExpired) {
                        existingClient.hasNonExpired = true;
                    }
                    existingGiftCard = currentGiftCard;
                }

                if (isEntered) {
                    existingClient.selectedGiftCard = existingGiftCard;
                }
            });
        }

        selectGiftCardForClient(client, preserveSelected = false) {
            if (preserveSelected && !!client.selectedGiftCard && _(client.giftCards).find(clientGC => clientGC.id === client.selectedGiftCard.id)) {
                return;
            }

            if (this.preselectGiftCardId) {
                const requestedGiftCard = _(client.giftCards).find(clientGC => clientGC.id === this.preselectGiftCardId);
                if (requestedGiftCard) {
                    client.selectedGiftCard = requestedGiftCard;
                    return requestedGiftCard;
                }
            }

            const selectedGiftCard = _(client.giftCards).find((giftCard) => {
                return giftCard.remainingAmount > 0 && (this.includeExpired || !giftCard.isExpired || !client.hasNonExpired);
            });
            if (selectedGiftCard) {
                // The card's state was already built on load; selecting it just shows that state.
                client.selectedGiftCard = selectedGiftCard;
            }
            return selectedGiftCard;
        }

        sort(giftCards) {
            return _(giftCards).chain()
                .sortBy((gc: any) => gc.endDate)
                .sortBy((gc: any) => gc.startDate)
                .sortBy((gc: any) => gc.isExpired)
                .value();
        }

        clientChanged() {
            if (this.selectedClient) {
                this.addClients([this.selectedClient.id]);
                this.selectedClient = undefined;
            }
        }

        includeExpiredChange() {
            // Reload the sale's clients plus any already-shown clients so the new includeExpired filter
            // takes effect, in a single query.
            const shownClientIds = this.clientsWithGiftCards.map(client => client.id);
            this.clientsWithGiftCards = [];
            this.addClients(_.union(this.getSaleClientIds(), shownClientIds));
        }

        giftCardNumberChange = _.debounce((event?: JQueryEventObject) => { this.giftCardNumberChangeWorker(event); }, 200, true);

        private giftCardNumberChangeWorker(event?: JQueryEventObject) {
            if (event && event.type == 'keydown') {
                const htmlElem = (event.srcElement as HTMLElement) || (event.target as HTMLElement);
                htmlElem.blur();
                return;
            }

            if (!this.giftCardNumber || String(this.giftCardNumber).trim() === '') {
                return;
            }

            const request: any = {
                pageNumber: 0,
                itemsPerPage: 10000,
                criteria: {
                    giftCardNumber: this.giftCardNumber,
                    includeExpired: !this.canUseExpired || this.includeExpired
                }
            };

            this.busyService.start();
            this.dataService.getListPaged('clientGiftCard', request)
                .then((response: any) => {
                    // Only non-liability promotional cards are valid here; filter the result client-side.
                    const promoCards = _(response).filter((card: any) => this.isNonLiabilityPromo(card));
                    if (_(promoCards).any()) {
                        if (!this.canUseExpired && promoCards.filter(card => !card.isExpired).length < 1) {
                            this.securityService.showDenyWarning(this.expiredGiftCardSecurityKeyPath,
                                Components.Common.SecurityKeys.REGISTER_REGISTER_ACTION_USEEXPIREDGIFTCARD);
                        } else {
                            const allowedGiftCards = _(promoCards).reject((card: any) =>
                                !this.registerGiftCardService.checkGCUsageRestriction(card, this.canUseExpired));
                            if (_(allowedGiftCards).any()) {
                                this.addGiftCards(allowedGiftCards, true);
                            }
                        }
                    } else {
                        this.modalService.alert("MESSAGE_PROMOTIONAL_GIFT_CARD_NOT_FOUND");
                    }
                })
                .finally(() => {
                    this.giftCardNumber = undefined;
                    this.busyService.stop();
                });
        }

        private getSaleClientIds(): string[] {
            return _(this.registerContext.sale.saleLines).chain()
                .where({ clientType: Model.ClientTypeEnum.Client })
                .pluck('clientId')
                .unique()
                .value();
        }

        updateClients() {
            this.addClients(this.getSaleClientIds());
        }

        private sortClients() {
            const order = this.getSaleClientIds();
            this.clientsWithGiftCards = _(this.clientsWithGiftCards).sortBy((client: any) => {
                const index = _(order).indexOf(client.id);
                return index === -1 ? order.length : index;
            });
        }

        private buildCardState(card) {
            card.saleTypeAllowed = this.getSaleTypeAllowed(card);
            card.eligibleItems = this.buildEligibleItems(card, card.saleTypeAllowed);
            _(card.eligibleItems).each(entry => {
                entry.isSelected = this.isSaleLineDiscountedByCard(entry.item, card);
            });
            card.eligibleItemsByClient = this.groupItemsByClient(card.eligibleItems);
            card.shouldSelectAll = _(card.eligibleItems).any()
                && _(card.eligibleItems).all(entry => entry.isSelected);
        }

        private isSaleLineDiscountedByCard(saleLine: Model.SaleLine, card): boolean {
            return !!card
                && !!saleLine.discountLines
                && _(saleLine.discountLines).any(dl => !!dl.entityId && dl.entityId === card.id);
        }

        private isNonLiabilityPromo(card): boolean {
            return !!card && card.isPromo && !card.isPromoLiability;
        }

        private hasPromoService(card): boolean {
            return !!card.promoServiceId && card.promoServiceId != Guid.empty.toString();
        }

        private isDiscountStackingRuleApplicable(saleLine): boolean {
            return saleLine.saleType !== Model.SalesTypeEnum.ServiceCancellationFee
                && saleLine.saleType !== Model.SalesTypeEnum.ServiceNoShowFee;
        }

        private getSaleTypeAllowed(card): number[] {
            if (this.hasPromoService(card)) {
                return [Model.SalesTypeEnum.Service, Model.SalesTypeEnum.ServiceCancellationFee, Model.SalesTypeEnum.ServiceNoShowFee, Model.SalesTypeEnum.PackageServiceSwapFee];
            }

            switch (card.discountEntityTypeEnum) {
                case Model.DiscountEntityTypeEnum.Package:
                    return [Model.SalesTypeEnum.Package];
                case Model.DiscountEntityTypeEnum.Product:
                    return [Model.SalesTypeEnum.Product];
                case Model.DiscountEntityTypeEnum.Service:
                    return [Model.SalesTypeEnum.Service, Model.SalesTypeEnum.ServiceCancellationFee, Model.SalesTypeEnum.ServiceNoShowFee, Model.SalesTypeEnum.PackageServiceSwapFee];
                case Model.DiscountEntityTypeEnum.ProductService:
                    return [Model.SalesTypeEnum.Product, Model.SalesTypeEnum.Service, Model.SalesTypeEnum.ServiceCancellationFee, Model.SalesTypeEnum.ServiceNoShowFee, Model.SalesTypeEnum.PackageServiceSwapFee];
                case Model.DiscountEntityTypeEnum.Fees:
                    return [Model.SalesTypeEnum.PackageServiceSwapFee, Model.SalesTypeEnum.PackageTransfer, Model.SalesTypeEnum.MembershipPayment];
                case Model.DiscountEntityTypeEnum.GiftCard:
                    return [Model.SalesTypeEnum.GiftCard];
                case Model.DiscountEntityTypeEnum.Membership:
                    return [Model.SalesTypeEnum.Membership];
                case Model.DiscountEntityTypeEnum.RebookedAppointments:
                    // Rebooked isn't its own saleType — it's a flag on a Service line; narrowed in buildEligibleItems.
                    return [Model.SalesTypeEnum.Service];
                default:
                    return [];
            }
        }

        /** Fresh per-card wrappers (one per unit of quantity) so each client's selection is independent. */
        private buildEligibleItems(card: Model.GiftCardType, saleTypeAllowed: number[]): any[] {
            let eligible: any[] = [];
            _(this.registerContext.sale.saleLines).each(saleLine => {
                if (!this.registerContext.isDiscountable(saleLine)) {
                    return;
                }
                if (!_(saleTypeAllowed).contains(saleLine.saleType)) {
                    return;
                }

                // Item-on-sale / yield management lines can't be further discounted by a promotional gift card.
                if (saleLine.discountLines && _(saleLine.discountLines).any((discountLine: any) =>
                    discountLine.saleLineDiscountType === Model.SaleLineDiscountTypeEnum.ItemOnSale ||
                    discountLine.saleLineDiscountType === Model.SaleLineDiscountTypeEnum.YieldManagement)) {
                    return;
                }

                // A RebookedAppointments card targets rebooked Service lines only; isRebooked lives on the service item.
                if (card.discountEntityTypeEnum === Model.DiscountEntityTypeEnum.RebookedAppointments
                    && !(saleLine.item && (<any>saleLine.item).isRebooked)) {
                    return;
                }

                let saleLineItemId = saleLine.item && saleLine.item.id;
                if (this.hasPromoService(card)
                    && (!saleLineItemId || saleLineItemId != card.promoServiceId)) {
                    return;
                }
                for (let ndx = 0, len = saleLine.quantity; ndx < len; ndx++) {
                    eligible.push({ item: saleLine, isSelected: false });
                }
            });
            return eligible;
        }

        canShowSaleItems(card): boolean {
            return !!card && !!card.eligibleItems && _(card.eligibleItems).any();
        }

        selectSaleItem(card, saleItem) {
            saleItem.isSelected = !saleItem.isSelected;
            card.shouldSelectAll = _(card.eligibleItems).all(item => item.isSelected);
        }

        selectUnselectAllSaleItems(card) {
            _(card.eligibleItems).each(item => item.isSelected = card.shouldSelectAll);
        }

        // Number of this client's cards that have at least one item selected. Shown next to the
        // dropdown so the operator can see how many of the client's cards are currently in play.
        selectedCardCount(client): number {
            if (!client || !client.giftCards) {
                return 0;
            }
            return _(client.giftCards).filter(card =>
                !!card.eligibleItems && _(card.eligibleItems).any(entry => entry.isSelected)).length;
        }

        groupItemsByClient(eligibleItems: any[]): any[] {
            let groups: any[] = [];
            let byKey: { [key: string]: any } = {};
            _(eligibleItems).each((entry) => {
                let line = entry.item;
                let key = `${line.clientId}|${line.clientType}`;
                let group = byKey[key];
                if (!group) {
                    group = {
                        clientId: line.clientId,
                        displayName: line.client ? line.client.displayName : line.clientDisplayName,
                        items: []
                    };
                    byKey[key] = group;
                    groups.push(group);
                }
                group.items.push(entry);
            });
            return groups;
        }

        // Every loaded card across all clients. Each card keeps its own item-selection state (see
        // buildCardState), so switching the dropdown to another card doesn't discard the prior card's
        // selection — all cards with selections must be considered, not just client.selectedGiftCard.
        private allCards(): any[] {
            return _(this.clientsWithGiftCards).chain()
                .map((client: any) => client.giftCards)
                .flatten()
                .compact()
                .value();
        }

        hasChanges(): boolean {
            return _(this.allCards()).any(card => {
                if (!card.eligibleItems) {
                    return false;
                }
                return _(card.eligibleItems).any(entry => entry.isSelected !== this.isSaleLineDiscountedByCard(entry.item, card));
            });
        }

        applyAll() {
            const conflictingCard = this.findNonStackableConflict();
            if (conflictingCard) {
                this.modalService.yellowConfirm('MESSAGE_NON_STACKABLE_PROMO_GIFT_CARD_APPLY_CONFIRM')
                    .then(() => {
                        this.clearConflictingDiscounts();
                        this.doApplyAll();
                    });
            } else {
                this.doApplyAll();
            }
        }

        // Returns the first card whose selected items have a stacking conflict:
        //   • the card itself is non-stackable and the line already has any discount, OR
        //   • the line already carries a non-stackable discount (regardless of the card's own stackability).
        private findNonStackableConflict(): any {
            for (const card of this.allCards()) {
                if (!card.eligibleItems) {
                    continue;
                }
                const selectedSaleLines = _(card.eligibleItems)
                    .chain()
                    .filter((entry: any) => entry.isSelected)
                    .map((entry: any) => entry.item)
                    .uniq()
                    .value();
                const hasConflict = _(selectedSaleLines).any((saleLine: any) => {
                    if (!this.isDiscountStackingRuleApplicable(saleLine)) {
                        return false;
                    }
                    // Stackable DiscountCard lines (other promo gift cards) are not considered conflicts —
                    // non-stackable only blocks regular discounts, not sibling promo cards.
                    const otherDiscounts = _(saleLine.discountLines).filter((discountLine: any) =>
                        discountLine.entityId !== card.id &&
                        discountLine.saleLineDiscountType !== Model.SaleLineDiscountTypeEnum.DiscountCard
                    );
                    const hasAnyOther = _(otherDiscounts).any();
                    const hasNonStackableOther = _(otherDiscounts).any((dl: any) => !dl.isStackable);
                    return (card.allowDiscountStacking === false && hasAnyOther) || hasNonStackableOther;
                });
                if (hasConflict) {
                    return card;
                }
            }
            return null;
        }

        // Removes regular (non-promo-card) discounts from lines that conflict with a non-stackable card.
        // Called only when the user has confirmed the stacking conflict prompt.
        private clearConflictingDiscounts() {
            for (const card of this.allCards()) {
                if (!card.eligibleItems) {
                    continue;
                }
                const selectedSaleLines = _(card.eligibleItems)
                    .chain()
                    .filter((entry: any) => entry.isSelected)
                    .map((entry: any) => entry.item)
                    .uniq()
                    .value();
                _(selectedSaleLines).each((saleLine: any) => {
                    if (!this.isDiscountStackingRuleApplicable(saleLine) || !saleLine.discountLines) {
                        return;
                    }
                    const regularDiscounts = _(saleLine.discountLines).filter((dl: any) =>
                        dl.saleLineDiscountType !== Model.SaleLineDiscountTypeEnum.DiscountCard
                    );
                    const hasNonStackableRegular = _(regularDiscounts).any((dl: any) => !dl.isStackable);
                    if (card.allowDiscountStacking === false || hasNonStackableRegular) {
                        saleLine.discountLines = _(saleLine.discountLines).without(...regularDiscounts);
                        const autoDiscountIdsToRemove = _(regularDiscounts)
                            .chain()
                            .filter((dl: any) => dl.isAutoApply)
                            .pluck('discountId')
                            .value();
                        const existing = this.registerContext.sale.removedAutomaticDiscountIds || [];
                        this.registerContext.sale.removedAutomaticDiscountIds =
                            Array.from(new Set([...existing, ...autoDiscountIdsToRemove]));
                    }
                });
            }
        }

        // Distinct promo gift card instances already on the sale, in the order their discount lines first
        // appear. This is the current applied order that a re-apply must preserve.
        private existingPromoCardOrder(): string[] {
            const order: string[] = [];
            _(this.registerContext.sale.saleLines).each((saleLine: any) => {
                _(saleLine.discountLines || []).each((discountLine: any) => {
                    if (discountLine.saleLineDiscountType === Model.SaleLineDiscountTypeEnum.DiscountCard
                        && discountLine.entityId
                        && !_(order).contains(discountLine.entityId)) {
                        order.push(discountLine.entityId);
                    }
                });
            });
            return order;
        }

        // Ordering policy for apply: cards already on the sale keep their current relative order; cards not yet
        // on the sale are appended after, preserving the incoming client-then-dropdown order (allCards()). A
        // brand-new apply (nothing on the sale) has an empty existing order, so it falls back to pure
        // client/dropdown order. Stable sort keeps newcomers (equal rank) in their allCards() order.
        private orderCardsForApply(cards: any[]): any[] {
            const existingOrder = this.existingPromoCardOrder();
            return _(cards).sortBy((card: any) => {
                const index = _(existingOrder).indexOf(card.id);
                return index === -1 ? existingOrder.length : index;
            });
        }

        private doApplyAll() {
            // Stable apply order: cards already on the sale keep their position; newly-selected cards are
            // appended in client/dropdown order. Computed before clearCardDiscounts wipes the lines below.
            const cards = this.orderCardsForApply(this.allCards());

            // Re-applying recomputes from scratch: drop every existing line for these cards first.
            let appliedAny = this.clearCardDiscounts(cards);

            // One spread group per card (a single card may span several sale lines).
            const groupIdByCard: { [cardId: string]: string } = {};
            for (const card of cards) {
                groupIdByCard[card.id] = Guid.newGuid().toString();
            }

            // Selection is positional: eligibleItems holds one entry per unit of quantity, and the k-th
            // entry of every card refers to the same physical unit of the sale line. Collect the distinct
            // lines that have any selected unit.
            const targetLines: Model.SaleLine[] = [];
            for (const card of cards) {
                if (!card.eligibleItems) {
                    continue;
                }
                for (const entry of card.eligibleItems) {
                    if (entry.isSelected && !_(targetLines).contains(entry.item)) {
                        targetLines.push(entry.item);
                    }
                }
            }

            // For each line, group its units by card-set (see buildLineSegments) and peel one sale line per
            // segment off the original. Whatever quantity remains after peeling stays undiscounted.
            for (const line of targetLines) {
                let remaining: Model.SaleLine = line;
                for (const segment of this.buildLineSegments(line, cards)) {
                    let target: Model.SaleLine;
                    if (!remaining) {
                        continue; // defensive: segment counts can never exceed the line quantity
                    } else if (segment.count < remaining.quantity) {
                        const remainder = this.registerContext.splitSaleLine(remaining, segment.count);
                        target = remaining;
                        remaining = remainder;
                    } else {
                        target = remaining;
                        remaining = null;
                    }

                    for (const card of segment.cards) {
                        this.pushCardDiscount(target, card, groupIdByCard[card.id]);
                        appliedAny = true;
                    }
                }
            }

            if (appliedAny) {
                this.registerContext.recalculateSale(true);
            }
            this.registerContext.selectSection(RegisterSectionTypeEnum.Services);
        }

        // Drops every existing discount line belonging to the given cards from the sale, so a re-apply
        // recomputes from the current selection instead of stacking onto stale lines. Returns whether
        // anything was removed.
        private clearCardDiscounts(cards: any[]): boolean {
            let removedAny = false;
            for (const card of cards) {
                for (const saleLine of this.registerContext.sale.saleLines) {
                    if (saleLine.discountLines && _(saleLine.discountLines).any(dl => dl.entityId === card.id)) {
                        saleLine.discountLines = _(saleLine.discountLines).filter(dl => dl.entityId !== card.id);
                        removedAny = true;
                    }
                }
            }
            return removedAny;
        }

        // Groups a line's units by the exact set of cards applied to each unit. Selection is positional:
        // the k-th eligible entry of every card refers to the same physical unit, so units picked by the
        // same cards share a segment (two cards on the 1st unit → one qty-1 segment carrying both), while
        // units picked by different cards get separate segments (card A on the 1st, card B on the 2nd →
        // two qty-1 segments). Unclaimed units are omitted so they stay undiscounted. First-seen order.
        private buildLineSegments(line: Model.SaleLine, cards: any[]): { cards: any[]; count: number }[] {
            // Ordered per-unit selection flags for each card on this line.
            const perCardEntries = _(cards).map(card => ({
                card: card,
                entries: card.eligibleItems ? _(card.eligibleItems).filter(entry => entry.item === line) : []
            }));

            const segments: { cards: any[]; count: number }[] = [];
            const segmentBySignature: { [signature: string]: { cards: any[]; count: number } } = {};
            for (let unit = 0; unit < line.quantity; unit++) {
                const unitCards = _(perCardEntries)
                    .chain()
                    .filter(cardEntry => cardEntry.entries.length > unit && cardEntry.entries[unit].isSelected)
                    .map(cardEntry => cardEntry.card)
                    .value();
                if (!unitCards.length) {
                    continue; // unclaimed unit — leave undiscounted
                }
                const signature = _(unitCards).pluck('id').sort().join('|');
                let segment = segmentBySignature[signature];
                if (!segment) {
                    segment = { cards: unitCards, count: 0 };
                    segmentBySignature[signature] = segment;
                    segments.push(segment);
                }
                segment.count++;
            }
            return segments;
        }

        private pushCardDiscount(saleLine: Model.SaleLine, card: any, groupId: string) {
            if (!saleLine.discountLines) {
                saleLine.discountLines = [];
            }

            const discountLine: Model.DiscountLine = {
                entityId: card.id,
                discountSaleLineGroupId: groupId,
                discountValue: card.remainingAmount,
                type: Model.DiscountTypeEnum.AmountOff,
                isSpread: true,
                isStackable: card.allowDiscountStacking !== false,
                displayName: card.giftCardTypeDisplayName,
                saleLineDiscountType: Model.SaleLineDiscountTypeEnum.DiscountCard,
                showPoleMessage: Model.SaleLineStatusEnum.Added
            };

            saleLine.discountLines.push(discountLine);
        }
    }
}
